import React from 'react';
import { UsuarioProps } from '../../shared/types/usuarioProps';
import AutenticacaoController, {
  AUTH_TOKEN_NAME,
} from '../../controllers/AutenticacaoController';
import api from '../../services/Api';
import { AuthContext } from './AuthContext';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = React.useState<UsuarioProps | null>(
    null
  );
  const [showLoading, setShowLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [sessaoBloqueada, setSessaoBloqueada] = React.useState<boolean>(false);

  const signIn = async (
    login: string,
    senha: string,
    /* [28/10] Vem do botao "Encerrar a sessao anterior e entrar", que so aparece depois
       de um 409. Viaja no proprio login porque a senha precisa ser conferida junto: uma
       rota separada deixaria derrubar sessao alheia sabendo so o usuario. */
    forcar = false
  ): Promise<boolean> => {
    try {
      /* [22/10] Joga fora o token velho ANTES de tentar entrar.
         Sem isto o pedido de login sai carregando o Authorization da sessao anterior - o
         axios guarda o cabecalho e o token fica no localStorage -, e no servidor aquele
         token morto fazia o login ser recusado antes mesmo de ser lido. A API tambem foi
         corrigida para nao olhar a sessao no login; aqui e a outra ponta da mesma coisa,
         e ela vale por si: pedido de login nao tem por que levar credencial nenhuma. */
      api.defaults.headers.Authorization = '';
      try {
        localStorage.removeItem(AUTH_TOKEN_NAME);
      } catch {
        // navegador sem armazenamento: o cabecalho limpo acima ja resolve
      }

      setSessaoBloqueada(false);

      const result = await AutenticacaoController.Login({
        Login: login,
        Senha: senha,
        Forcar: forcar,
      } as any);

      /* [22/10] O Login NUNCA lanca - ele captura e devolve a mensagem. Entao o catch
         abaixo nunca via a recusa, e `result.Data.AccessToken` chegava indefinido: a tela
         dizia "login e/ou senha invalidos" mesmo quando a recusa era por sessao ativa.
         Agora o caso de erro e tratado AQUI, onde ele realmente chega. */
      if (result.StatusCode !== 200 || !result?.Data?.AccessToken) {
        const mensagem =
          typeof result?.Data === 'string'
            ? result.Data
            : 'Login e/ou senha inválidos.';

        setLoginError(mensagem);
        localStorage.setItem('loginError', mensagem);
        // 409 e a recusa por sessao viva - a unica que tem saida pela propria tela.
        setSessaoBloqueada(result.StatusCode === 409);
        return false;
      }

      api.defaults.headers.Authorization = `Bearer ${result.Data.AccessToken}`;
      const user = result.Data.User[0];

      setToken(result.Data.AccessToken);
      setCurrentUser({
        Ativo: user.Ativo,
        senha: '',
        usu_login: user.usu_login,
        usu_id: user.usu_id,
        usu_nome: user.usu_nome,
      });

      return true;
    } catch (error: any) {
      setLoginError(error?.response?.data?.message);
      localStorage.setItem('loginError', error?.response?.data?.message);
      return false;
    }
  };

  const logout = async () => {
    // Avisa o servidor ANTES de jogar o token fora: depois disso nao ha mais como provar
    // quem somos, e a sessao ficaria viva ate expirar por inatividade - segurando a vaga
    // e impedindo a propria pessoa de entrar de outra maquina.
    await AutenticacaoController.Sair();

    api.defaults.headers.Authorization = '';
    setToken('');
    setCurrentUser(null);
  };

  const setToken = (token: string) => {
    localStorage.setItem(AUTH_TOKEN_NAME, token);
  };

  const validateToken = async () => {
    const storagedToken = localStorage.getItem(AUTH_TOKEN_NAME);
    if (storagedToken) {
      try {
        setShowLoading(true);
        setShowLoading(false);
      } catch (error) {
        setToken('');
        setCurrentUser(null);
        setShowLoading(false);
        // window.location.href = '/'
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        signIn,
        sessaoBloqueada,
        logout,
        validateToken,
        showLoading,
        setShowLoading,
        loginError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
